import React, { useCallback, useState } from 'react';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Box, Grid, LinearProgress, Typography } from '@mui/material';
import Dropzone, { FileRejection } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Routes from 'routes';
import { v4 as uuidv4 } from 'uuid';
import API from 'api/API';
import Button from 'components/Button';
import { useLanguage } from 'context/AppContext';
import { FileService } from 'services/FileService';
import * as Styles from '../DeliveryPortalPage.styles';

interface FileUploadProps {}

const FileUpload: React.FC<FileUploadProps> = () => {
  const [language] = useLanguage();
  const [progress, setProgress] = useState(0);
  const [uploadState, setUploadState] = useState<'notStarted' | 'uploading' | 'complete' | 'error'>('notStarted');
  const [uploadText, setUploadText] = useState(
    `${language.DeliveryPortal.FileUpload.text} ${language.DeliveryPortal.FileUpload.browse}`,
  );
  const [uploadId, setUploadId] = useState<string>('');
  const [fileLocation, setFileLocation] = useState<string>('');
  const navigate = useNavigate();

  const handleDrop = useCallback(
    async (acceptedFiles: File[], error: FileRejection[]) => {
      const uuid = uuidv4();
      const fileName = acceptedFiles[0].name;
      const fileLocationString = `receiving_orders_file_uploads/${uuid}/${fileName}`;
      setFileLocation(fileLocationString);

      // Initial setup
      setProgress(0);
      setUploadState('notStarted');
      setUploadId(uuid);

      // Handle dropzone error
      if (error.length > 0 || !acceptedFiles[0].name.endsWith('.csv')) {
        toast.error(language.DeliveryPortal.FileUpload.error.fileType);
        API.errorHandler(new Error(error[0].errors[0].message));
        setUploadState('error');
        return;
      }

      try {
        setUploadState('uploading');
        setUploadText(fileName);

        // Upload the file directly to GCS through firebase
        await FileService.saveFileWithProgress(fileLocationString, acceptedFiles[0], setProgress);
      } catch (error) {
        setUploadText(`${language.DeliveryPortal.FileUpload.text} ${language.DeliveryPortal.FileUpload.browse}`);
        API.errorHandler(error);
        toast.error(language.DeliveryPortal.FileUpload.error.upload);
        setUploadState('error');
      }
    },
    [
      language.DeliveryPortal.FileUpload.browse,
      language.DeliveryPortal.FileUpload.error.fileType,
      language.DeliveryPortal.FileUpload.error.upload,
      language.DeliveryPortal.FileUpload.text,
    ],
  );

  const handleUpload = useCallback(async () => {
    const fullFileLocation = `${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/${fileLocation}`;
    try {
      await API.orders.uploadOrderDataV2(uploadId, fullFileLocation);
      toast.success(language.DeliveryPortal.FileUpload.success.upload);
      navigate(Routes.DeliveryPortalUploadList.path);
    } catch (error) {
      API.errorHandler(error);
      toast.error(language.DeliveryPortal.FileUpload.error.upload);
      setUploadState('error');
    }
  }, [
    fileLocation,
    navigate,
    language.DeliveryPortal.FileUpload.error.upload,
    language.DeliveryPortal.FileUpload.success.upload,
    uploadId,
  ]);

  const handleCancel = useCallback(async () => {
    setProgress(0);
    setUploadText(`${language.DeliveryPortal.FileUpload.text} ${language.DeliveryPortal.FileUpload.browse}`);
  }, [language.DeliveryPortal.FileUpload.browse, language.DeliveryPortal.FileUpload.text]);
  return (
    <Styles.Wrapper>
      <Dropzone onDrop={handleDrop} accept="" maxFiles={1}>
        {({ getRootProps, getInputProps }) => (
          <Styles.FileUploadContainer error={uploadState === 'error'}>
            <Styles.FileUploadRoot {...getRootProps()}>
              <input {...getInputProps()} />
              <Grid container justifyContent="center">
                <Grid item xs={12} textAlign="center" mb={4}>
                  <FileCopyIcon sx={{ fontSize: 80 }} />
                </Grid>
                <Grid item xs={12} textAlign="center">
                  <Typography variant="subtitle1" gutterBottom>
                    {uploadText}
                  </Typography>
                </Grid>
                <Grid item xs={12} textAlign="center">
                  <Typography variant="subtitle2">{language.DeliveryPortal.FileUpload.note}</Typography>
                </Grid>
              </Grid>
            </Styles.FileUploadRoot>
          </Styles.FileUploadContainer>
        )}
      </Dropzone>
      <Styles.ProgressBarWrapper>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ width: '100%', mr: 1 }}>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
          <Box sx={{ minWidth: 35 }}>
            <Typography variant="body2" color="text.secondary">{`${progress}%`}</Typography>
          </Box>
        </Box>
      </Styles.ProgressBarWrapper>
      <Styles.FileUploadButtonsContainer>
        <Button onClick={handleCancel}>{language.DeliveryPortal.FileUpload.cancel}</Button>
        <Button disabled={progress >= 100 ? false : true} onClick={handleUpload}>
          {language.DeliveryPortal.FileUpload.submit}
        </Button>
      </Styles.FileUploadButtonsContainer>
    </Styles.Wrapper>
  );
};

export default FileUpload;
